import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { API, fmtPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import Seo from "@/components/Seo";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [order, setOrder] = useState(null);
  const { clear } = useCart();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    const poll = async () => {
      try {
        const { data } = await API.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setStatus("paid");
          clear();
          return;
        }
        if (data.status === "failed" || data.status === "expired") { setStatus("failed"); return; }
        if (++attempts.current < 12) setTimeout(poll, 2000);
        else setStatus("timeout");
      } catch {
        if (++attempts.current < 12) setTimeout(poll, 2000);
        else setStatus("error");
      }
    };
    poll();
  }, [sessionId, clear]);

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-10 py-24 sm:py-32 text-center" data-testid="payment-success-page">
      <Seo title="Order Confirmed" />
      {status === "checking" && (
        <>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-stone2 animate-pulse">Confirming with the card company…</p>
          <p className="font-serif text-4xl text-ink mt-6">Wrapping your cards in tissue</p>
        </>
      )}
      {status === "paid" && (
        <>
          <CheckCircle2 size={40} className="text-sage mx-auto" />
          <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-6">Thank you — order confirmed</h1>
          <p className="text-stone2 mt-4 text-sm sm:text-base leading-relaxed max-w-md mx-auto">
            Your notecards will be flat-packed and mailed within 2 studio days. A receipt is on its way to your inbox.
          </p>
          {order && (
            <div className="mt-8 inline-block bg-paper border border-ink/10 deckle px-8 py-6 text-left" data-testid="order-summary">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-sage">Order total</p>
              <p className="font-serif text-3xl text-ink mt-1">{fmtPrice(order.amount)}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone2 mt-3">{order.items?.join(" · ")}</p>
            </div>
          )}
          <div className="mt-10">
            <Link to="/portfolio" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] bg-ink text-linen rounded-full px-7 py-3.5 hover:bg-terracotta transition-colors">
              Continue to the Gallery <ArrowRight size={14} />
            </Link>
          </div>
        </>
      )}
      {(status === "failed" || status === "timeout" || status === "error") && (
        <>
          <XCircle size={40} className="text-terracotta mx-auto" />
          <h1 className="font-serif text-4xl text-ink mt-6">We couldn&rsquo;t confirm that payment</h1>
          <p className="text-stone2 mt-4 text-sm max-w-md mx-auto">
            {status === "timeout" ? "The confirmation is taking unusually long — if your card was charged, the order will still arrive. " : ""}
            Please try again or write to the studio.
          </p>
          <Link to="/notecards" className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] border border-ink/20 rounded-full px-7 py-3.5 hover:bg-ink hover:text-linen transition-colors">
            Back to Notecards
          </Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-10 py-24 sm:py-32 text-center" data-testid="payment-cancel-page">
      <Seo title="Checkout Cancelled" />
      <h1 className="font-serif text-4xl sm:text-5xl text-ink">No harm done</h1>
      <p className="text-stone2 mt-4 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
        Your bag is kept exactly as you left it. Checkout again whenever you&rsquo;re ready.
      </p>
      <Link to="/notecards" data-testid="cancel-back-link"
        className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] bg-ink text-linen rounded-full px-7 py-3.5 hover:bg-terracotta transition-colors">
        Return to the Shop <ArrowRight size={14} />
      </Link>
    </div>
  );
}
